import { useState, useEffect } from "react";
import { showAlert } from '@/components/AppDialog';
import { useNavigate } from "react-router-dom";
import { BarChart3, BookOpen } from "lucide-react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { API_BASE_URL } from "@/utils/env";
import { getAuthTokens } from "@/utils/auth";
import Sidebar from "../components/Sidebar";
import MobileNav from "../components/MobileNav";
import UserProfileMenu from "../components/UserProfileMenu";
import { useAuth } from "../hooks/useAuth";
import { useSetupPricing } from "@/data/setupPricing";
import { purchasedPackages } from '@/utils/purchases';

// ─── Types ─────────────────────────────────────────────────────────────────
type Flags = [number, number, number, number, number]; // [books, vatReg, vatFiling, ctReg, ctFiling]

// ─── Constants ─────────────────────────────────────────────────────────────

function DirhamSymbol({ em = '0.85' }: { em?: string }) {
    return (
        <svg viewBox="0 0 24 24" aria-label="AED" style={{ width:`${em}em`, height:`${em}em`, display:'inline-block', verticalAlign:'-0.04em', marginRight:'2px', flexShrink:0 }}>
            <path d="M5.5 4.2c0-.5.4-.9.9-.9h4.2c4.7 0 7.9 3.3 7.9 8.5s-3.2 8.7-7.9 8.7H6.4c-.5 0-.9-.4-.9-.9V4.2z" fill="none" stroke="currentColor" strokeWidth="2.1" />
            <line x1="2.5" y1="10.2" x2="16.5" y2="10.2" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
            <line x1="2.5" y1="14.2" x2="16.5" y2="14.2" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
        </svg>
    );
}

const DFLT_FEES = { vatReg: 500, vatFiling: 1000, ctReg: 500, ctFiling: 2000 };
const DFLT_BANDS: Record<string, number> = {
    'Up to 100': 1500, '100 – 200': 2000, '200 – 500': 3000, '500+': 5000,
};
const BAND_KEYS = ['Up to 100', '100 – 200', '200 – 500', '500+'];

const FEATURED = [
    { flags: [1,1,1,1,1] as Flags, label: 'Full',      desc: 'Bookkeeping, VAT and Corporate Tax – fully managed.',              popular: true  },
    { flags: [1,1,1,0,0] as Flags, label: 'Books + VAT', desc: 'Bookkeeping with VAT registration and filing.',                  popular: false },
    { flags: [1,0,0,1,1] as Flags, label: 'Books + CT',  desc: 'Bookkeeping with Corporate Tax registration and filing.',        popular: false },
    { flags: [0,1,1,1,1] as Flags, label: 'Tax only',    desc: 'VAT and Corporate Tax handled – no bookkeeping.',               popular: false },
];

const INCLUDE_NAMES = [
    'Bookkeeping', 'VAT Registration', 'VAT Filing',
    'Corporate Tax Registration', 'Corporate Tax Filing',
];

// ─── Corporate services & compliance (à-la-carte, from corporateServices/current) ──
type CorpAddon = { label: string; note: string; price: number | null; mainlandOnly: boolean; category?: string };
const CORP_CATS = ['Compliance', 'Visas', 'PRO & government', 'Wills & POA'];
const DFLT_CORP_ADDONS: CorpAddon[] = [
    { label: 'goAML Registration', note: 'UAE FIU portal · entity + compliance officer', price: 1000, mainlandOnly: false, category: 'Compliance' },
    { label: 'Employment visa', note: 'Entry permit + e-channel', price: 2625, mainlandOnly: false, category: 'Visas' },
    { label: 'Family visa', note: 'New / renewal', price: 5250, mainlandOnly: false, category: 'Visas' },
    { label: "Dependent / Maid's visa", note: 'Excl. deposit', price: 5000, mainlandOnly: false, category: 'Visas' },
    { label: 'Golden visa', note: '10-year · investor/talent', price: null, mainlandOnly: false, category: 'Visas' },
    { label: 'Green visa', note: 'Freelancer / skilled', price: null, mainlandOnly: false, category: 'Visas' },
    { label: 'Document attestation', note: 'MOFA / embassy / notary', price: 525, mainlandOnly: false, category: 'PRO & government' },
    { label: 'NOC & gov letters', note: 'Per NOC', price: 525, mainlandOnly: false, category: 'PRO & government' },
    { label: 'PO box & mail', note: 'Annual', price: 525, mainlandOnly: false, category: 'PRO & government' },
    { label: 'Payroll & WPS', note: 'Mainland only · monthly', price: null, mainlandOnly: true, category: 'PRO & government' },
    { label: 'POA attestation', note: 'Notary / MOFA', price: 368, mainlandOnly: false, category: 'Wills & POA' },
    { label: 'Expat will (DIFC)', note: 'Partner law firm', price: null, mainlandOnly: false, category: 'Wills & POA' },
];

// ─── Combo generation (mirrors HTML JS exactly) ─────────────────────────────
// CODES bit positions: 0=VR, 1=VF, 2=CR, 3=CF, 4=B
const CODES = ['VR','VF','CR','CF','B'];
const RANK: Record<string,number> = { VR:0, VF:1, CR:2, CF:3, B:4 };

function toFlags(set: string[]): Flags {
    return [
        set.includes('B')  ? 1 : 0,
        set.includes('VR') ? 1 : 0,
        set.includes('VF') ? 1 : 0,
        set.includes('CR') ? 1 : 0,
        set.includes('CF') ? 1 : 0,
    ] as Flags;
}

function sameSet(a: string[], b: string[]) { return a.length===b.length && a.every(x=>b.includes(x)); }

function combTier(set: string[]) {
    if (set.length===1) return 1;
    if (set.length===2) {
        if (sameSet(set,['VR','VF'])) return 2;
        if (sameSet(set,['CR','CF'])) return 2.1;
        return 3;
    }
    return set.length + 1;
}

function rankKey(set: string[]) { return set.map(c=>RANK[c]).sort((x,y)=>(x??0)-(y??0)).join(''); }

function pkgName(f: Flags): string {
    const [b,vr,vf,cr,cf] = f;
    if (b&&vr&&vf&&cr&&cf) return 'Full – Books + VAT + CT';
    const parts: string[] = [];
    if (vr&&vf) parts.push('VAT (Reg + Filing)');
    else if (vr) parts.push('VAT Registration');
    else if (vf) parts.push('VAT Filing');
    if (cr&&cf) parts.push('Corporate Tax (Reg + Filing)');
    else if (cr) parts.push('Corporate Tax Registration');
    else if (cf) parts.push('Corporate Tax Filing');
    if (b) parts.push('Bookkeeping');
    return parts.join(' + ');
}

function keyOf(f: Flags) { return f.join(''); }

interface Combo { flags: Flags; name: string; key: string; }

const COMBOS: Combo[] = (() => {
    const sets: string[][] = [];
    for (let m = 1; m < 32; m++) {
        const set: string[] = [];
        CODES.forEach((c,i) => { if (m & (1<<i)) set.push(c); });
        sets.push(set);
    }
    sets.sort((a,b) => combTier(a)-combTier(b) || rankKey(a).localeCompare(rankKey(b)));
    return sets.map(set => { const f = toFlags(set); return { flags: f, name: pkgName(f), key: keyOf(f) }; });
})();

// ─── Small helpers ─────────────────────────────────────────────────────────
function fromPrice(f: Flags, fees: typeof DFLT_FEES) {
    return (f[1] ? fees.vatReg : 0) + (f[3] ? fees.ctReg : 0);
}

/* Every service on this page is priced in dirhams — no conversion, no currency switch. */
function moneyStr(aed: number): string {
    return 'AED ' + aed.toLocaleString('en-US');
}

// ─── Micro-components ──────────────────────────────────────────────────────
function Money({ aed }: { aed: number }) {
    return <><DirhamSymbol em="0.8"/>{aed.toLocaleString('en-US')}</>;
}

function Tick() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="#15824E" strokeWidth="2.6" style={{ width:18, height:18, flexShrink:0 }}>
            <path d="M5 13l4 4L19 7"/>
        </svg>
    );
}

function Dash() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="#C4CAD1" strokeWidth="2.6" style={{ width:18, height:18, flexShrink:0 }}>
            <path d="M6 12h12"/>
        </svg>
    );
}

// ─── Component ─────────────────────────────────────────────────────────────
export default function Services() {
    const navigate = useNavigate();
    const { logout, user, refreshAuth } = useAuth();

    const [selected, setSelected] = useState<Flags>([1,1,1,1,1]);
    const [band, setBand] = useState('100 – 200');
    const [fees, setFees] = useState(DFLT_FEES);
    const [bands, setBands] = useState(DFLT_BANDS);
    const [purchased, setPurchased] = useState(false);
    const [saving, setSaving] = useState(false);

    // Service line: Accounting & tax  ·  Corporate services & compliance
    const [activeTab, setActiveTab] = useState<'acct' | 'corp'>('acct');
    const [corpAddons, setCorpAddons] = useState<CorpAddon[]>(DFLT_CORP_ADDONS);
    const [corpSel, setCorpSel] = useState<Record<number, boolean>>({});
    const [corpSaving, setCorpSaving] = useState(false);
    const [corpDone, setCorpDone] = useState(false);
    // §4: hide the Accounting tab when the client's setup plan already bundles accounting.
    const [acctIncluded, setAcctIncluded] = useState(false);
    const [acctPlanName, setAcctPlanName] = useState('');
    // Why the Accounting tab is hidden, and one-time-purchase ownership.
    const [acctReason, setAcctReason] = useState<'included' | 'entry-only' | 'owned' | ''>('');
    // Which onboarding the client came through. Decides whether the entry pack is named
    // "Starter" (incorporate) or "Pulse" (operate), and which plan chooser to send them
    // back to — those are two different flows and two different pages.
    const [setupFlow, setSetupFlow] = useState<'incorporate' | 'operate' | ''>('');
    const SP = useSetupPricing();
    const [ownedCorp, setOwnedCorp] = useState<Set<string>>(new Set());

    // Live pricing from Firestore pricing/current
    useEffect(() => {
        const ref = doc(db, 'pricing', 'current');
        return onSnapshot(
            ref,
            snap => {
                if (!snap.exists()) return;
                const d = snap.data();
                if (d.vatReg)  setFees({ vatReg:d.vatReg, vatFiling:d.vatFiling, ctReg:d.ctReg, ctFiling:d.ctFiling });
                if (d.bands)   setBands(d.bands);
            },
            err => {
                if (err.name !== 'AbortError') console.warn('Pricing snapshot error:', err);
            }
        );
    }, []);

    // Live corporate catalog from Firestore corporateServices/current
    useEffect(() => {
        const ref = doc(db, 'corporateServices', 'current');
        return onSnapshot(
            ref,
            snap => {
                const d = snap.data();
                if (d && Array.isArray(d.addons) && d.addons.length) { setCorpAddons(d.addons); setCorpSel({}); }
            },
            err => { if (err.name !== 'AbortError') console.warn('Corporate snapshot error:', err); }
        );
    }, []);

    // §4 — Accounting & tax à-la-carte is only for the ENTRY packs (Starter / Pulse):
    // higher packs (Compliance / Business-in-a-Box) already bundle it, and it can only
    // be bought ONCE. Corporate services stay available to everyone. Also flags which
    // services are already owned so they can't be repurchased.
    useEffect(() => {
        if (!user?.uid) return;
        let cancelled = false;
        (async () => {
            try {
                const idToken = getAuthTokens()?.idToken;
                const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, { headers: idToken ? { Authorization: `Bearer ${idToken}` } : {} });
                if (!resp.ok) return;
                const json = await resp.json();
                const u = json?.success ? json.user : json;
                if (cancelled) return;
                const bsd = u?.businessSetupData || {};
                const key = String(bsd.planKey || '').toLowerCase();
                const acctAvailable = ['starter', 'pulse'].includes(key); // entry packs only

                // One-time purchase: what has this client already bought?
                const pkgs = purchasedPackages(u);
                const ownsAcct = pkgs.some((p: any) => Array.isArray(p?.flags)); // an accounting package
                const corpLabels = new Set<string>();
                pkgs.forEach((p: any) => { if (Array.isArray(p?.services)) p.services.forEach((s: string) => corpLabels.add(String(s))); });

                setOwnedCorp(corpLabels);
                setAcctPlanName(bsd.planName || 'your package');
                // `type` is written by the onboarding; fall back to inferring it from the
                // plan key, since `starter` only exists in the incorporate flow and
                // `pulse` only in the operate flow.
                const t = String(bsd.type || '').toLowerCase();
                setSetupFlow(
                    t === 'incorporate' || t === 'operate' ? t
                    : key === 'pulse' ? 'operate'
                    : key === 'starter' ? 'incorporate'
                    : ''
                );

                if (!acctAvailable || ownsAcct) {
                    setAcctIncluded(true); // hide the Accounting tab
                    setAcctReason(ownsAcct ? 'owned' : (['compliance', 'business-in-a-box'].includes(key) ? 'included' : 'entry-only'));
                    setActiveTab('corp');
                }
            } catch { /* non-blocking */ }
        })();
        return () => { cancelled = true; };
    }, [user?.uid]);

    // Name the ONE entry pack that applies to this client rather than listing both:
    // Pulse is only offered in the operate flow, Starter only in the incorporate flow.
    // Titles are read from setupPricing so an admin rename flows through to this notice.
    const entryPlan = setupFlow === 'operate' ? SP.operatePlans.find(p => p.id === 'pulse')
        : setupFlow === 'incorporate' ? SP.setupPlans.find(p => p.id === 'starter')
        : undefined;
    const entryPlanLabel = entryPlan?.title
        || (setupFlow === 'operate' ? 'Pulse' : setupFlow === 'incorporate' ? 'Starter' : 'Starter / Pulse');
    // …and send them back to the chooser for the flow they actually came through.
    const changePlanPath = setupFlow === 'operate' ? '/operate-plan' : '/setup-plan';

    const [books, vatReg, vatFiling, ctReg, ctFiling] = selected;
    const booksOn  = books === 1;
    const bandFee  = bands[band] ?? 0;

    let once = 0, month = 0, qtr = 0, year = 0;
    if (books)     month += bandFee;
    if (vatReg)    once  += fees.vatReg;
    if (vatFiling) qtr   += fees.vatFiling;
    if (ctReg)     once  += fees.ctReg;
    if (ctFiling)  year  += fees.ctFiling;

    // Adaptive headline (same logic as the HTML)
    let heroVal = 0, heroUnit = '', heroKey = '';
    if (month > 0)      { heroVal = month; heroUnit = '/ month';        heroKey = 'm'; }
    else if (once > 0)  { heroVal = once;  heroUnit = 'one-time setup'; heroKey = 'o'; }
    else if (qtr > 0)   { heroVal = qtr;   heroUnit = '/ quarter';      heroKey = 'q'; }
    else if (year > 0)  { heroVal = year;  heroUnit = '/ year';         heroKey = 'y'; }

    const subParts: string[] = [];
    if (once  > 0 && heroKey !== 'o') subParts.push(moneyStr(once) + ' one-time setup');
    if (month > 0 && heroKey !== 'm') subParts.push(moneyStr(month) + ' /mo');
    if (qtr   > 0 && heroKey !== 'q') subParts.push(moneyStr(qtr) + ' /qtr');
    if (year  > 0 && heroKey !== 'y') subParts.push(moneyStr(year) + ' /yr');

    const currentKey = keyOf(selected);
    const currentIdx = COMBOS.findIndex(c => c.key === currentKey);

    const LINE_DEFS = [
        { i:0, n:'Bookkeeping',                   freq: band + ' / month', fee: bandFee,        b:'m' },
        { i:1, n:'VAT Registration',              freq:'one-time',          fee: fees.vatReg,   b:'o' },
        { i:2, n:'VAT Filing',                    freq:'per quarter',       fee: fees.vatFiling, b:'q' },
        { i:3, n:'Corporate Tax Registration',    freq:'one-time',          fee: fees.ctReg,    b:'o' },
        { i:4, n:'Corporate Tax Filing',          freq:'per year',          fee: fees.ctFiling,  b:'y' },
    ];

    const activeLines = LINE_DEFS.filter(d => selected[d.i] === 1);

    // ── Corporate services derived ──
    const corpCats = (() => {
        const seen: string[] = [];
        corpAddons.forEach(a => { const c = (a.category || 'Other').trim() || 'Other'; if (!seen.includes(c)) seen.push(c); });
        return seen.sort((a, b) => { const ia = CORP_CATS.indexOf(a), ib = CORP_CATS.indexOf(b); return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b); });
    })();
    // Exclude already-owned services — they can't be repurchased (one-time).
    const corpSelected = corpAddons.map((a, i) => ({ a, i })).filter(o => corpSel[o.i] && !ownedCorp.has(o.a.label));
    const corpTotal = corpSelected.reduce((s, { a }) => s + (a.price ?? 0), 0);
    const corpReqCount = corpSelected.filter(({ a }) => a.price == null).length;

    const submitCorpRequest = async () => {
        if (!user || !corpSelected.length) return;
        setCorpSaving(true);
        try {
            await refreshAuth().catch(() => {});
            const tokensStr = localStorage.getItem('authTokens');
            const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
            const labels = corpSelected.map(({ a }) => a.label);
            const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}/service`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
                body: JSON.stringify({
                    name: `Corporate services · ${labels.join(', ')}`,
                    kind: 'corporate',
                    services: labels,
                    purchasedAt: new Date().toISOString(),
                }),
            });
            if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error((err as any).error || `Server error (${resp.status})`); }
        } catch (err: any) {
            console.error('Failed to submit corporate request:', err);
            showAlert('Could not send your request: ' + (err.message || 'Unknown error') + '\nPlease try again.');
            setCorpSaving(false);
            return;
        }
        setCorpSaving(false);
        setCorpDone(true);
    };

    const cardStyle: React.CSSProperties = {
        background: '#fff',
        border: '1px solid #E7EAEE',
        borderRadius: 18,
        boxShadow: '0 1px 2px rgba(23,38,58,.04)',
        overflow: 'hidden',
    };

    if (purchased) {
        const SVC_LABELS = ['Bookkeeping', 'VAT Registration', 'VAT Filing', 'Corporate Tax Registration', 'Corporate Tax Filing'];
        const includedSvcs = SVC_LABELS.filter((_, i) => selected[i] === 1);

        // 40 confetti particles with varied colours, sizes, positions, delays
        const CONF_COLORS = ['#FFD54F','#22C55E','#60A5FA','#F472B6','#A78BFA','#FB923C','#34D399','#FBBF24','#F87171','#38BDF8'];
        const confetti = Array.from({ length: 40 }, (_, i) => ({
            left: `${3 + (i * 2.43) % 94}%`,
            color: CONF_COLORS[i % CONF_COLORS.length],
            delay: `${(i * 0.07).toFixed(2)}s`,
            dur: `${2.4 + (i % 7) * 0.2}s`,
            size: 6 + (i % 5) * 2,
            shape: i % 3,   // 0=circle, 1=rect, 2=triangle
            spin: 200 + (i % 6) * 120,
        }));

        const floatEmojis = ['🎉','🎊','✨','🥳','🎈','⭐'];
        return (
            <div style={{ position:'fixed', inset:0, background:'linear-gradient(135deg,#060d1a 0%,#0d1f2d 55%,#081a0f 100%)', fontFamily:"'Montserrat',system-ui,sans-serif" }}>
                <style>{`
                    @keyframes confFall2 {
                        0%   { transform:translateY(-40px) rotate(0deg) scale(1);   opacity:1; }
                        80%  { opacity:1; }
                        100% { transform:translateY(108vh) rotate(var(--spin,720deg)) scale(0.5); opacity:0; }
                    }
                    @keyframes floatUp2 {
                        0%   { transform:translateY(20px) scale(.6); opacity:0; }
                        15%  { opacity:.9; }
                        100% { transform:translateY(-90px) scale(1.1); opacity:0; }
                    }
                    @keyframes burstIn {
                        0%   { transform:scale(0) rotate(-15deg); opacity:0; }
                        50%  { transform:scale(1.22) rotate(5deg);  opacity:1; }
                        75%  { transform:scale(0.93) rotate(-2deg); }
                        100% { transform:scale(1) rotate(0deg);     opacity:1; }
                    }
                    @keyframes pulseGlow {
                        0%,100% { box-shadow:0 0 0 0 rgba(34,197,94,0); }
                        50%     { box-shadow:0 0 0 18px rgba(34,197,94,0.12); }
                    }
                    @keyframes drawCheckCel { from { stroke-dashoffset:50; } to { stroke-dashoffset:0; } }
                    @keyframes riseUp2 { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
                    @keyframes starPop {
                        0%   { transform:scale(0) rotate(0deg);   opacity:0; }
                        60%  { transform:scale(1.3) rotate(180deg); opacity:1; }
                        100% { transform:scale(1) rotate(360deg);  opacity:1; }
                    }
                    @keyframes shimmer { 0%,100% { opacity:0.6; transform:scale(1); } 50% { opacity:1; transform:scale(1.05); } }
                    @keyframes spinSlow { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
                `}</style>

                {/* ── Decorative layer (fixed, clipped, non-interactive) ── */}
                <div style={{ position:'absolute', inset:0, overflow:'hidden', pointerEvents:'none' }}>
                    {/* Ambient glow orbs */}
                    <div style={{ position:'absolute', width:500, height:500, borderRadius:'50%', left:'50%', top:'30%', transform:'translate(-50%,-50%)', background:'radial-gradient(circle, rgba(34,197,94,0.10) 0%, transparent 65%)', animation:'shimmer 3s ease-in-out infinite' }} />
                    <div style={{ position:'absolute', width:300, height:300, borderRadius:'50%', background:'radial-gradient(circle, rgba(255,213,79,0.08) 0%, transparent 65%)', top:'8%', right:'8%', animation:'shimmer 4s 1s ease-in-out infinite' }} />
                    {/* Confetti rain */}
                    {confetti.map((p, i) => (
                        <div key={i} style={{
                            position:'absolute', top:'-40px', left:p.left,
                            width: p.shape === 1 ? p.size * 1.6 : p.size,
                            height: p.shape === 1 ? p.size * 0.6 : p.size,
                            borderRadius: p.shape === 0 ? '50%' : p.shape === 1 ? '2px' : '0',
                            background: p.shape === 2
                                ? `linear-gradient(60deg, ${p.color} 0%, ${p.color} 50%, transparent 50%)`
                                : p.color,
                            animation: `confFall2 ${p.dur} ${p.delay} ease-in infinite`,
                            '--spin': `${p.spin}deg`,
                            opacity: 0.9,
                        } as any} />
                    ))}
                    {/* Floating celebratory emojis rising from the bottom */}
                    {Array.from({ length: 9 }, (_, i) => (
                        <div key={`fe${i}`} style={{
                            position:'absolute', bottom:'-30px', left:`${8 + (i * 10.5) % 86}%`,
                            fontSize: 18 + (i % 3) * 8,
                            animation:`floatUp2 ${4 + (i % 4)}s ${(i * 0.5).toFixed(1)}s ease-in infinite`,
                        }}>{floatEmojis[i % floatEmojis.length]}</div>
                    ))}
                </div>

                {/* ── Scrollable content layer (centers when it fits, scrolls when it doesn't) ── */}
                <div style={{ position:'absolute', inset:0, overflowY:'auto', display:'flex', padding:'clamp(16px,4vh,48px) 16px', WebkitOverflowScrolling:'touch' }}>
                    <div style={{ position:'relative', margin:'auto', background:'rgba(255,255,255,0.055)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'clamp(20px,4vw,28px)', padding:'clamp(30px,5vw,46px) clamp(22px,4vw,40px)', maxWidth:520, width:'100%', boxShadow:'0 40px 80px rgba(0,0,0,0.55)', textAlign:'center' }}>

                        {/* Check mark */}
                        <div style={{ position:'relative', display:'inline-flex', alignItems:'center', justifyContent:'center', marginBottom:'clamp(20px,4vw,28px)' }}>
                            <div style={{ position:'absolute', inset:-4, borderRadius:'50%', background:'radial-gradient(circle, rgba(34,197,94,0.2) 0%, transparent 70%)', animation:'pulseGlow 2s ease-in-out infinite' }} />
                            <div style={{ width:'clamp(76px,18vw,90px)', height:'clamp(76px,18vw,90px)', borderRadius:'50%', background:'linear-gradient(135deg,rgba(34,197,94,0.3),rgba(34,197,94,0.1))', border:'2px solid rgba(34,197,94,0.55)', display:'flex', alignItems:'center', justifyContent:'center', animation:'burstIn 0.65s cubic-bezier(0.34,1.56,0.64,1) forwards', position:'relative' }}>
                                <svg width="42" height="42" viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                                        style={{ strokeDasharray:50, strokeDashoffset:50, animation:'drawCheckCel 0.45s 0.5s ease forwards' }} />
                                </svg>
                            </div>
                        </div>

                        {/* Headline */}
                        <div style={{ fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', color:'rgba(255,255,255,0.4)', fontWeight:700, marginBottom:10, animation:'riseUp2 0.45s 0.55s both' }}>
                            Package Confirmed 🎉
                        </div>
                        <h1 style={{ fontSize:'clamp(30px,7vw,42px)', fontWeight:900, margin:'0 0 8px', letterSpacing:'-.03em', lineHeight:1.1, animation:'riseUp2 0.45s 0.65s both', background:'linear-gradient(120deg,#ffffff 0%,#FFE9A8 55%,#FFD54F 100%)', WebkitBackgroundClip:'text', backgroundClip:'text', color:'transparent' }}>
                            You're all set!
                        </h1>
                        <p style={{ color:'rgba(255,255,255,0.55)', fontSize:'clamp(13px,3.4vw,14.5px)', fontWeight:500, margin:'0 0 22px', animation:'riseUp2 0.45s 0.72s both' }}>
                            Your accounting package is now active. 🚀
                        </p>

                        {/* Package pill */}
                        <div style={{ display:'inline-flex', alignItems:'center', gap:10, background:'rgba(255,213,79,0.12)', border:'1px solid rgba(255,213,79,0.3)', borderRadius:999, padding:'9px 20px', marginBottom:22, animation:'riseUp2 0.45s 0.78s both', maxWidth:'100%' }}>
                            <BarChart3 className="w-4 h-4" style={{ color:'#FFD54F', flexShrink:0 }} />
                            <span style={{ fontSize:13.5, fontWeight:800, color:'rgba(255,255,255,0.92)' }}>{pkgName(selected)}</span>
                        </div>

                        {/* Included services */}
                        <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:16, padding:'18px 20px', marginBottom:16, textAlign:'left', animation:'riseUp2 0.45s 0.84s both' }}>
                            <p style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'.1em', color:'rgba(255,255,255,0.35)', fontWeight:700, margin:'0 0 14px' }}>What's included</p>
                            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                                {includedSvcs.map((s, idx) => (
                                    <div key={s} style={{ display:'flex', alignItems:'center', gap:10, animation:`riseUp2 0.3s ${0.88 + idx * 0.07}s both` }}>
                                        <div style={{ width:20, height:20, borderRadius:'50%', background:'rgba(34,197,94,0.2)', border:'1px solid rgba(34,197,94,0.4)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                                <path d="M5 13l4 4L19 7" stroke="#22C55E" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                        <span style={{ fontSize:13.5, fontWeight:600, color:'rgba(255,255,255,0.78)' }}>{s}</span>
                                    </div>
                                ))}
                                {selected[0] === 1 && (
                                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                        <div style={{ width:20, height:20, borderRadius:'50%', background:'rgba(255,213,79,0.15)', border:'1px solid rgba(255,213,79,0.3)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><BookOpen style={{ width:11, height:11, color:'#FFD54F' }} /></div>
                                        <span style={{ fontSize:13.5, fontWeight:600, color:'rgba(255,255,255,0.78)' }}>{band} transactions / month</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* What's next */}
                        <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:16, padding:'18px 20px', marginBottom:24, textAlign:'left', animation:'riseUp2 0.45s 1.0s both' }}>
                            <p style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'.1em', color:'rgba(255,255,255,0.35)', fontWeight:700, margin:'0 0 14px' }}>What happens next</p>
                            <div style={{ display:'flex', flexDirection:'column', gap:13 }}>
                                {[
                                    { n:'01', text:'Our team reaches out within 24 hours' },
                                    { n:'02', text:'We guide you through onboarding step by step' },
                                    { n:'03', text:'Your services go live and filing begins' },
                                ].map(item => (
                                    <div key={item.n} style={{ display:'flex', alignItems:'center', gap:14 }}>
                                        <span style={{ width:26, height:26, borderRadius:'50%', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, color:'#FFD54F', flexShrink:0 }}>
                                            {item.n}
                                        </span>
                                        <span style={{ fontSize:13, color:'rgba(255,255,255,0.58)', fontWeight:600 }}>{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* CTAs */}
                        <div style={{ display:'flex', gap:10, flexWrap:'wrap', animation:'riseUp2 0.45s 1.08s both' }}>
                            <button
                                onClick={() => navigate('/dashboard')}
                                style={{ flex:'1 1 200px', background:'linear-gradient(135deg,#FFD54F,#E6B400)', color:'#17263A', border:'none', borderRadius:13, padding:'15px 0', fontSize:14, fontWeight:800, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 8px 28px rgba(230,180,0,.35)', transition:'transform .15s,box-shadow .15s' }}
                                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 12px 36px rgba(230,180,0,.45)'; }}
                                onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 8px 28px rgba(230,180,0,.35)'; }}
                            >
                                Go to Dashboard →
                            </button>
                            <button
                                onClick={() => navigate('/my-services')}
                                style={{ flex:'0 1 auto', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', color:'rgba(255,255,255,0.65)', borderRadius:13, padding:'15px 22px', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit', transition:'background .15s' }}
                                onMouseEnter={e => (e.currentTarget.style.background='rgba(255,255,255,0.13)')}
                                onMouseLeave={e => (e.currentTarget.style.background='rgba(255,255,255,0.07)')}
                            >
                                View Services
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ display:'flex', minHeight:'100vh', background:'#F5F6F8', fontFamily:"'Montserrat',system-ui,sans-serif" }}>
            <style>{`
                .svc-lay { display:grid; grid-template-columns:1fr 350px; gap:22px; align-items:start; }
                .svc-2c  { grid-template-columns:1fr 1fr; }
                .svc-sticky { position:sticky; top:24px; }
                @media (max-width: 980px) {
                    .svc-lay { grid-template-columns:1fr; }
                    .svc-2c  { grid-template-columns:1fr; }
                    .svc-sticky { position:static; }
                }
            `}</style>
            <Sidebar />
            <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }} className="md:ms-64">
                <MobileNav onLogout={logout} />

                {/* ── Top bar ── */}
                <header style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, padding:'20px 32px', borderBottom:'1px solid #E7EAEE', background:'#fff' }}>
                    <div>
                        <h1 style={{ fontSize:24, fontWeight:800, letterSpacing:'-.02em', color:'#17263A' }}>Services &amp; Packages</h1>
                        <div style={{ color:'#6E7888', fontSize:13.5, fontWeight:500, marginTop:2 }}>
                            {activeTab === 'acct'
                                ? 'Bookkeeping, VAT and Corporate Tax: pick any combination and your price updates instantly.'
                                : 'Company setup, visas, PRO and compliance registrations: add any service à la carte.'}
                        </div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:16 }}>
                        <UserProfileMenu />
                    </div>
                </header>

                {/* ── Service-line sub-tabs ── */}
                <div style={{ display:'flex', gap:2, padding:'0 32px', background:'#fff', borderBottom:'1px solid #E7EAEE', overflowX:'auto' }}>
                    {([
                        { id:'acct' as const, label:'Accounting & tax', accent:'#E6B400' },
                        { id:'corp' as const, label:'Corporate services & compliance', accent:'#F08A24' },
                    ].filter(t => !(t.id === 'acct' && acctIncluded))).map(t => {
                        const on = activeTab === t.id;
                        const cnt = t.id === 'corp' ? corpSelected.length : 0;
                        return (
                            <button key={t.id} onClick={() => setActiveTab(t.id)}
                                style={{ position:'relative', border:'none', background:'none', fontFamily:'inherit', fontWeight:700, fontSize:14, color: on ? '#17263A' : '#6E7888', padding:'15px 14px', cursor:'pointer', display:'flex', alignItems:'center', gap:8, whiteSpace:'nowrap' }}>
                                {t.label}
                                {cnt > 0 && <span style={{ background:'#F08A24', color:'#fff', borderRadius:999, minWidth:19, height:19, padding:'0 6px', fontSize:10.5, fontWeight:800, display:'inline-flex', alignItems:'center', justifyContent:'center' }}>{cnt}</span>}
                                <span style={{ position:'absolute', left:10, right:10, bottom:-1, height:3, borderRadius:'3px 3px 0 0', background: on ? t.accent : 'transparent' }} />
                            </button>
                        );
                    })}
                </div>

                {/* ── Content ── */}
                <div style={{ padding:'28px 32px 56px', maxWidth:1060, width:'100%', margin:'0 auto' }}>

                    {acctIncluded && (
                        <div style={{ background:'#E6F2EC', border:'1px solid #BFE3CF', borderRadius:12, padding:'12px 16px', marginBottom:20, fontSize:13.5, color:'#0F6E42', fontWeight:600 }}>
                            {acctReason === 'owned' ? (
                                <>You already have an accounting package. Manage it in{' '}
                                <span style={{ textDecoration:'underline', cursor:'pointer' }} onClick={() => navigate('/my-services')}>My Services</span>. Add Corporate services below.</>
                            ) : acctReason === 'entry-only' ? (
                                <>Accounting &amp; tax à-la-carte is available on the {entryPlanLabel} plan. Add Corporate services below, or{' '}
                                <span style={{ textDecoration:'underline', cursor:'pointer' }} onClick={() => navigate(changePlanPath)}>change your package</span>.</>
                            ) : (
                                <>Accounting &amp; tax is included in your {acctPlanName} package, no need to add it again. Add Corporate services below, or{' '}
                                <span style={{ textDecoration:'underline', cursor:'pointer' }} onClick={() => navigate(changePlanPath)}>change your package</span>.</>
                            )}
                        </div>
                    )}

                    {/* ── Corporate services & compliance pane ── */}
                    {activeTab === 'corp' && (
                        <div className="svc-lay">
                            {/* Left: grouped à-la-carte services */}
                            <div style={cardStyle}>
                                <div style={{ height:5, background:'linear-gradient(90deg,#F08A24,#FFD54F)' }} />
                                <div style={{ padding:'24px 26px' }}>
                                    <div style={{ fontSize:11.5, textTransform:'uppercase', letterSpacing:'.07em', color:'#6E7888', fontWeight:700, marginBottom:4 }}>Choose the services you need</div>
                                    {corpCats.map(cat => {
                                        const items = corpAddons.map((a,i)=>({a,i})).filter(o => ((o.a.category||'Other').trim()||'Other')===cat);
                                        if (!items.length) return null;
                                        return (
                                            <div key={cat} style={{ marginTop:20 }}>
                                                <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:10 }}>
                                                    <span style={{ background:'#FDE8CF', color:'#C76B12', borderRadius:6, padding:'3px 9px', fontSize:10, fontWeight:800, letterSpacing:'.05em', textTransform:'uppercase' }}>{cat}</span>
                                                    <span style={{ fontSize:11.5, color:'#6E7888', fontWeight:700 }}>{items.length} service{items.length>1?'s':''}</span>
                                                </div>
                                                <div className="svc-2c" style={{ display:'grid', gap:10 }}>
                                                    {items.map(({a,i}) => {
                                                        const owned = ownedCorp.has(a.label);
                                                        const on = !owned && !!corpSel[i];
                                                        return (
                                                            <button key={i} disabled={owned} onClick={() => { if (owned) return; setCorpSel(prev => ({ ...prev, [i]: !prev[i] })); setCorpDone(false); }}
                                                                style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 13px', border:`1.5px solid ${owned ? '#BFE3CF' : on ? '#F08A24' : '#E7EAEE'}`, borderRadius:11, background: owned ? '#F1FAF5' : '#fff', cursor: owned ? 'default' : 'pointer', fontFamily:'inherit', textAlign:'left', width:'100%', boxShadow: on ? '0 0 0 3px #FDE8CF' : 'none', opacity: owned ? 0.9 : 1 }}>
                                                                <span style={{ width:19, height:19, borderRadius:6, border:`1.5px solid ${owned ? '#15824E' : on ? '#C76B12' : '#E7EAEE'}`, flex:'none', display:'flex', alignItems:'center', justifyContent:'center', background: (owned || on) ? (owned ? '#15824E' : '#F08A24') : '#fff' }}>
                                                                    {(owned || on) && <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.4" style={{ width:12, height:12 }}><path d="M5 13l4 4L19 7"/></svg>}
                                                                </span>
                                                                <span style={{ flex:1, minWidth:0 }}>
                                                                    <b style={{ display:'block', fontWeight:700, fontSize:13.5, lineHeight:1.3, color:'#17263A' }}>{a.label}{a.mainlandOnly && <span style={{ background:'#F5F6F8', color:'#6E7888', border:'1px solid #E7EAEE', borderRadius:5, padding:'1px 5px', fontSize:9, fontWeight:800, textTransform:'uppercase', marginLeft:6, verticalAlign:1 }}>mainland</span>}</b>
                                                                    <small style={{ color:'#6E7888', fontWeight:600, fontSize:11.5 }}>{a.note}</small>
                                                                </span>
                                                                <span style={{ fontWeight:800, whiteSpace:'nowrap', fontSize: owned ? 11 : (a.price==null?11.5:13.5), color: owned ? '#15824E' : (a.price==null?'#6E7888':'#17263A') }}>
                                                                    {owned ? 'Owned' : (a.price==null ? 'On request' : <Money aed={a.price} />)}
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Right: sticky summary */}
                            <div className="svc-sticky">
                                <div style={cardStyle}>
                                    <div style={{ height:5, background:'linear-gradient(90deg,#F08A24,#FFD54F)' }} />
                                    <div style={{ padding:'22px' }}>
                                        <h3 style={{ fontSize:13, color:'#6E7888', textTransform:'uppercase', letterSpacing:'.06em', fontWeight:700 }}>Corporate selection</h3>
                                        <div style={{ color:'#17263A', fontWeight:800, fontSize:17, margin:'4px 0 16px', lineHeight:1.3 }}>
                                            {corpSelected.length ? `${corpSelected.length} service${corpSelected.length>1?'s':''} selected` : '–'}
                                        </div>
                                        <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
                                            <span style={{ fontSize:32, fontWeight:900, letterSpacing:'-.03em', color:'#17263A' }}>{corpTotal ? <Money aed={corpTotal}/> : '–'}</span>
                                            <span style={{ fontSize:12, color:'#6E7888', fontWeight:700 }}>{corpTotal ? 'one-time' : ''}</span>
                                        </div>
                                        <div style={{ color:'#42505F', fontSize:13, fontWeight:600, marginTop:4 }}>
                                            {!corpSelected.length ? 'Nothing selected yet' : corpReqCount ? `${corpReqCount} item${corpReqCount>1?'s':''} quoted on request` : 'no other fees'}
                                        </div>
                                        <div style={{ marginTop:16, paddingTop:14, borderTop:'1px solid #EDEFF2' }}>
                                            {corpSelected.length === 0 ? (
                                                <div style={{ color:'#AEB5BD', fontWeight:600, fontSize:13, padding:'8px 0' }}>Select a service to see pricing</div>
                                            ) : corpSelected.map(({a,i}) => (
                                                <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'6px 0', color:'#42505F', fontWeight:600 }}>
                                                    <span>{a.label}</span>
                                                    <b style={{ color:'#17263A', fontWeight:800, whiteSpace:'nowrap' }}>{a.price==null ? 'On request' : <Money aed={a.price}/>}</b>
                                                </div>
                                            ))}
                                        </div>
                                        {corpDone ? (
                                            <div style={{ marginTop:18, background:'#EAF7EF', border:'1px solid #BFE6CE', color:'#15824E', borderRadius:11, padding:'12px 14px', fontSize:13, fontWeight:700 }}>
                                                ✓ Request sent. Our team will confirm next steps.
                                            </div>
                                        ) : (
                                            <button onClick={submitCorpRequest} disabled={!corpSelected.length || corpSaving}
                                                style={{ border:'none', borderRadius:11, fontFamily:'inherit', fontWeight:800, fontSize:15, padding:'14px 26px', cursor: corpSelected.length && !corpSaving ? 'pointer':'not-allowed', display:'flex', alignItems:'center', justifyContent:'center', gap:8, background:'#FFD54F', color:'#17263A', boxShadow:'0 8px 20px rgba(230,180,0,.32)', width:'100%', marginTop:18, opacity: corpSelected.length ? 1 : .5 }}>
                                                {corpSaving ? 'Sending…' : 'Request these services'}
                                            </button>
                                        )}
                                        <div style={{ marginTop:12, background:'#FFF6DC', border:'1px solid #F0E0A6', color:'#7A5B00', borderRadius:10, padding:'10px 12px', fontSize:11.5, fontWeight:600, lineHeight:1.45 }}>
                                            Prices exclude government fees. Items shown as “On request” are quoted after review and are not included in the total.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="svc-lay" style={{ display: activeTab==='acct' ? undefined : 'none' }}>

                        {/* ── Left column ── */}
                        <div>

                            {/* Card 1: Choose package */}
                            <div style={{ ...cardStyle, marginBottom:22 }}>
                                <div style={{ height:5, background:'linear-gradient(90deg,#FFD54F,#E6B400)' }} />
                                <div style={{ padding:'24px 26px' }}>

                                    <div style={{ fontSize:11.5, textTransform:'uppercase', letterSpacing:'.07em', color:'#6E7888', fontWeight:700, marginBottom:14 }}>
                                        1 · Choose your package
                                    </div>

                                    {/* 2×2 featured cards */}
                                    <div className="svc-2c" style={{ display:'grid', gap:14 }}>
                                        {FEATURED.map((pkg, pi) => {
                                            const isSel = keyOf(pkg.flags) === currentKey;
                                            const fp    = fromPrice(pkg.flags, fees);
                                            return (
                                                <button
                                                    key={pi}
                                                    onClick={() => { setSelected(pkg.flags.slice() as Flags); setPurchased(false); }}
                                                    style={{
                                                        position:'relative', textAlign:'left', background:'#fff',
                                                        border: `1.5px solid ${isSel ? '#E6B400' : '#E7EAEE'}`,
                                                        borderRadius:15, padding:'18px 18px 16px',
                                                        cursor:'pointer', fontFamily:'inherit',
                                                        boxShadow: isSel ? '0 0 0 3px rgba(230,180,0,.20)' : 'none',
                                                        transition:'border-color .15s,box-shadow .15s',
                                                        width:'100%',
                                                    }}
                                                >
                                                    {pkg.popular && (
                                                        <span style={{ position:'absolute', top:-9, right:14, background:'#17263A', color:'#fff', fontSize:9.5, fontWeight:800, letterSpacing:'.05em', textTransform:'uppercase', padding:'4px 9px', borderRadius:999 }}>
                                                            Most popular
                                                        </span>
                                                    )}
                                                    {/* Check circle */}
                                                    <span style={{ position:'absolute', top:14, right:14, width:20, height:20, borderRadius:'50%', border:`1.5px solid ${isSel ? '#E6B400' : '#E7EAEE'}`, background: isSel ? '#FFD54F' : '#fff', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                        {isSel && (
                                                            <svg viewBox="0 0 24 24" fill="none" stroke="#17263A" strokeWidth="3" style={{ width:12, height:12 }}>
                                                                <path d="M5 13l4 4L19 7"/>
                                                            </svg>
                                                        )}
                                                    </span>

                                                    <div style={{ fontWeight:800, fontSize:15.5, color:'#17263A' }}>{pkg.label}</div>
                                                    <div style={{ color:'#6E7888', fontSize:12.5, marginTop:4, lineHeight:1.5, minHeight:34 }}>{pkg.desc}</div>
                                                    <div style={{ marginTop:12, fontSize:12, color:'#42505F', fontWeight:600 }}>
                                                        from{' '}
                                                        <b style={{ fontSize:17, fontWeight:800, color:'#17263A' }}>
                                                            <Money aed={fp} />
                                                        </b>{' '}
                                                        setup
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* All-combos dropdown */}
                                    <div style={{ marginTop:16 }}>
                                        <label htmlFor="svc-all" style={{ display:'block', fontSize:11.5, fontWeight:700, color:'#6E7888', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>
                                            Or pick any combination (all packages)
                                        </label>
                                        <select
                                            id="svc-all"
                                            value={currentIdx >= 0 ? currentIdx : 0}
                                            onChange={e => { const c = COMBOS[+e.target.value]; if (c) { setSelected(c.flags.slice() as Flags); setPurchased(false); } }}
                                            style={{ width:'100%', appearance:'none', border:'1.5px solid #E7EAEE', borderRadius:11, padding:'12px 40px 12px 14px', fontSize:14.5, fontWeight:700, color:'#17263A', background:'#fff', fontFamily:'inherit', cursor:'pointer' }}
                                        >
                                            {COMBOS.map((c,i) => <option key={i} value={i}>{c.name}</option>)}
                                        </select>
                                    </div>

                                    {/* Options row */}
                                    <div style={{ display:'flex', gap:22, flexWrap:'wrap', marginTop:18, paddingTop:18, borderTop:'1px solid #EDEFF2' }}>
                                        {/* Volume */}
                                        <div>
                                            <label htmlFor="svc-band" style={{ display:'block', fontSize:11.5, fontWeight:700, color:'#6E7888', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>
                                                Bookkeeping volume
                                            </label>
                                            <select
                                                id="svc-band"
                                                value={band}
                                                onChange={e => setBand(e.target.value)}
                                                disabled={!booksOn}
                                                style={{ appearance:'none', border:'1.5px solid #E7EAEE', borderRadius:10, padding:'10px 38px 10px 13px', fontSize:14, fontWeight:700, color: booksOn ? '#17263A' : '#9AA2AC', background: booksOn ? '#fff' : '#F4F6F9', fontFamily:'inherit', cursor: booksOn ? 'pointer' : 'not-allowed', minWidth:200 }}
                                            >
                                                {BAND_KEYS.map(k => <option key={k} value={k}>{k} / mo</option>)}
                                            </select>
                                        </div>

                                    </div>
                                </div>
                            </div>

                            {/* Card 2: What's included + breakdown */}
                            <div style={cardStyle}>
                                <div style={{ padding:'24px 26px' }}>

                                    {/* What's included */}
                                    <div style={{ fontSize:11.5, textTransform:'uppercase', letterSpacing:'.07em', color:'#6E7888', fontWeight:700, marginBottom:14 }}>
                                        What's included
                                    </div>
                                    <div className="svc-2c" style={{ display:'grid', gap:9 }}>
                                        {INCLUDE_NAMES.map((name, i) => {
                                            const on = selected[i] === 1;
                                            return (
                                                <div key={i} style={{ display:'flex', alignItems:'center', gap:9, fontSize:13.5, fontWeight:600, padding:'9px 12px', border:`1px solid ${on ? '#F0E0A6' : '#E7EAEE'}`, borderRadius:10, background: on ? '#fff' : '#F5F6F8', color: on ? '#17263A' : '#AEB5BD' }}>
                                                    {on ? <Tick /> : <Dash />}
                                                    {name}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Price breakdown */}
                                    <div style={{ fontSize:11.5, textTransform:'uppercase', letterSpacing:'.07em', color:'#6E7888', fontWeight:700, marginTop:22 }}>
                                        Price breakdown
                                    </div>
                                    <div>
                                        {activeLines.length === 0 ? (
                                            <div style={{ display:'flex', justifyContent:'space-between', padding:'11px 0', fontSize:14 }}>
                                                <span style={{ fontWeight:600, color:'#AEB5BD' }}>Select a package to see pricing</span>
                                            </div>
                                        ) : (
                                            activeLines.map((d, idx) => (
                                                <div key={d.i} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'11px 0', borderBottom: idx < activeLines.length-1 ? '1px dashed #E7EAEE' : 'none', fontSize:14 }}>
                                                    <span>
                                                        <span style={{ fontWeight:600, color:'#17263A' }}>{d.n}</span>
                                                        <span style={{ fontSize:11.5, color:'#6E7888', fontWeight:600, marginLeft:7 }}>{d.freq}</span>
                                                    </span>
                                                    <span style={{ fontWeight:800, whiteSpace:'nowrap', color:'#17263A' }}>
                                                        <Money aed={d.fee} />
                                                    </span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Right column: sticky summary ── */}
                        <div className="svc-sticky">
                            <div style={cardStyle}>
                                <div style={{ height:5, background:'linear-gradient(90deg,#FFD54F,#E6B400)' }} />
                                <div style={{ padding:'22px' }}>

                                    <h3 style={{ fontSize:13, color:'#6E7888', textTransform:'uppercase', letterSpacing:'.06em', fontWeight:700 }}>
                                        Your selection
                                    </h3>
                                    <div style={{ color:'#17263A', fontWeight:800, fontSize:17, margin:'4px 0 16px', lineHeight:1.3 }}>
                                        {pkgName(selected)}
                                    </div>

                                    {/* Hero price */}
                                    <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
                                        <span style={{ fontSize:32, fontWeight:900, letterSpacing:'-.03em', color:'#17263A' }}>
                                            {heroVal > 0 ? <Money aed={heroVal} /> : '–'}
                                        </span>
                                        <span style={{ fontSize:12, color:'#6E7888', fontWeight:700 }}>{heroUnit}</span>
                                    </div>
                                    <div style={{ color:'#42505F', fontSize:13, fontWeight:600, marginTop:4 }}>
                                        {subParts.length ? 'plus ' + subParts.join(' · ') : 'no other fees'}
                                    </div>

                                    {/* Breakdown rows */}
                                    <div style={{ marginTop:16, paddingTop:14, borderTop:'1px solid #EDEFF2' }}>
                                        {once > 0 && (
                                            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'6px 0', color:'#42505F', fontWeight:600 }}>
                                                <span>One-time setup (registrations)</span>
                                                <b style={{ color:'#17263A', fontWeight:800 }}><Money aed={once}/></b>
                                            </div>
                                        )}
                                        {month > 0 && (
                                            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'6px 0', color:'#42505F', fontWeight:600 }}>
                                                <span>Bookkeeping</span>
                                                <b style={{ color:'#17263A', fontWeight:800 }}><Money aed={month}/> /mo</b>
                                            </div>
                                        )}
                                        {qtr > 0 && (
                                            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'6px 0', color:'#42505F', fontWeight:600 }}>
                                                <span>VAT filing</span>
                                                <b style={{ color:'#17263A', fontWeight:800 }}><Money aed={qtr}/> /qtr</b>
                                            </div>
                                        )}
                                        {year > 0 && (
                                            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'6px 0', color:'#42505F', fontWeight:600 }}>
                                                <span>Corporate Tax filing</span>
                                                <b style={{ color:'#17263A', fontWeight:800 }}><Money aed={year}/> /yr</b>
                                            </div>
                                        )}
                                    </div>

                                    {/* Purchase button */}
                                    <button
                                        onClick={async () => {
                                            if (!user) return;
                                            setSaving(true);
                                            try {
                                                // Refresh the ID token so it's valid for the PUT call
                                                await refreshAuth().catch(() => {});
                                                const tokensStr = localStorage.getItem('authTokens');
                                                const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
                                                const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}/service`, {
                                                    method: 'POST',
                                                    headers: {
                                                        'Content-Type': 'application/json',
                                                        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
                                                    },
                                                    body: JSON.stringify({
                                                        name: pkgName(selected),
                                                        flags: Array.from(selected),
                                                        band,
                                                        purchasedAt: new Date().toISOString(),
                                                    }),
                                                });
                                                if (!resp.ok) {
                                                    const err = await resp.json().catch(() => ({}));
                                                    throw new Error((err as any).error || `Server error (${resp.status})`);
                                                }
                                            } catch (err: any) {
                                                console.error('Failed to save purchase:', err);
                                                showAlert('Could not save your package: ' + (err.message || 'Unknown error') + '\nPlease try again.');
                                                setSaving(false);
                                                return;
                                            }
                                            setSaving(false);
                                            setPurchased(true);
                                        }}
                                        disabled={saving}
                                        style={{ border:'none', borderRadius:11, fontFamily:'inherit', fontWeight:800, fontSize:15, padding:'14px 26px', cursor: saving ? 'wait' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, background: saving ? '#E8C843' : '#FFD54F', color:'#17263A', boxShadow:'0 8px 20px rgba(230,180,0,.32)', width:'100%', marginTop:18, transition:'transform .15s, background .15s', opacity: saving ? 0.8 : 1 }}
                                        onMouseEnter={e => { if (!saving) e.currentTarget.style.transform='translateY(-1px)'; }}
                                        onMouseLeave={e => (e.currentTarget.style.transform='none')}
                                    >
                                        {saving ? 'Confirming…' : 'Purchase package'}
                                    </button>

                                    {/* Trust badges */}
                                    <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:6 }}>
                                        {['Cancel or change anytime','Filed by a registered FTA tax agent','No setup fee'].map(t => (
                                            <div key={t} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#42505F', fontWeight:600 }}>
                                                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.4" style={{ width:15, height:15, flexShrink:0, stroke:'#15824E' }}>
                                                    <path d="M5 13l4 4L19 7"/>
                                                </svg>
                                                {t}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}
